import React, { ReactElement, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import dayjs from 'dayjs'
import { AddIcon, DeleteIcon } from 'src/v3/core/icons'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  deletePendingANCCEvent,
  deletePendingANCCCourse,
  deleteANCCEvent,
  deleteANCCCourse,
  finalizePendingANCCEvent,
  finalizePendingANCCCourse,
  clearAnccOperationStatus,
  selectAnccOperationStatus,
  selectAnccOperationError,
  selectAnccOperationSuccess,
} from 'src/v3/content-pipeline/accreditation.slice'
import ConfirmAction from 'src/v3/components/confirm-action'
import { ANCCPendingAccreditation, ANCCFinalizedAccreditation } from './types'
import { CreateForm } from './create-form'
import { EditForm } from './edit-form'
import { FieldComparison, StatusActions, AutoTrackedDates } from '../shared'
import {
  getStatusColor,
  getAccreditationFlags,
  RejectedAlert,
  ApprovedAlert,
  OperationSnackbars,
  calculateFieldChanges,
} from '../../shared-utils'

type ANCCSectionProps = {
  eventId?: string
  courseId?: string
  finalized?: ANCCFinalizedAccreditation
  pending?: ANCCPendingAccreditation
}

export let ANCCSection = ({
  eventId,
  courseId,
  finalized,
  pending,
}: ANCCSectionProps): ReactElement => {
  let dispatch = useAppDispatch()
  let [formOpen, setFormOpen] = useState(false)
  let [editFormOpen, setEditFormOpen] = useState(false)
  let [expanded, setExpanded] = useState(false)
  let [confirm, setConfirm] = useState('')

  let operationStatus = useAppSelector(selectAnccOperationStatus)
  let operationError = useAppSelector(selectAnccOperationError)
  let operationSuccess = useAppSelector(selectAnccOperationSuccess)

  let submitting = operationStatus === 'pending'
  let { hasNoAccreditation, hasOnlyFinalized, hasOnlyPending, hasBoth } =
    getAccreditationFlags(finalized, pending)
  let statusColor = getStatusColor(pending?.status)

  let handleFinalize = () => {
    if (!pending?.id) return
    if (eventId) {
      dispatch(finalizePendingANCCEvent({ id: pending.id, eventId }))
    } else if (courseId) {
      dispatch(finalizePendingANCCCourse({ id: pending.id, courseId }))
    }
  }

  let changes = calculateFieldChanges(finalized, pending, [
    { key: 'courseCode', label: 'Course Code' },
    { key: 'expirationDate', label: 'Expiration Date' },
  ])

  return (
    <Box>
      <Card variant="outlined" sx={{ overflow: 'visible' }}>
        <Box className="p-4 grid grid-cols-[1fr_auto] gap-4 items-center">
          <Box className="flex items-center gap-4">
            <Typography variant="h6">ANCC Accreditation</Typography>
            {finalized && (
              <>
                <Chip color="success" label="Finalized" size="small" />
                <Typography color="text.secondary" variant="body2">
                  {finalized.courseCode}
                  {finalized.expirationDate &&
                    ` • Exp ${dayjs(finalized.expirationDate).format('MMM D, YYYY')}`}
                </Typography>
              </>
            )}
            {hasOnlyPending && pending && (
              <>
                <Chip
                  label={pending.status}
                  size="small"
                  sx={{ color: statusColor }}
                />
                <Typography color="text.secondary" variant="body2">
                  {pending.courseCode || 'Pending'}
                </Typography>
              </>
            )}
            {hasNoAccreditation && (
              <Chip color="default" label="Not Created" size="small" />
            )}
          </Box>

          <Box className="flex items-center gap-2">
            {hasBoth && pending && (
              <Chip
                color="warning"
                label={`${changes.length} ${changes.length === 1 ? 'Change' : 'Changes'}`}
                size="small"
              />
            )}
            {hasBoth && pending && (
              <Chip
                label={pending.status}
                size="small"
                sx={{ color: statusColor }}
              />
            )}
            {(hasNoAccreditation || hasOnlyFinalized) && (
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                size="small"
                onClick={() => setFormOpen(true)}
              >
                {hasNoAccreditation ? 'Create' : 'New Update'}
              </Button>
            )}
            {(finalized || pending) && (
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                aria-label={expanded ? 'Show less' : 'Show more'}
              >
                {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
          </Box>
        </Box>

        {pending?.status === 'Rejected' && (
          <RejectedAlert
            rejectedDate={pending.rejectedDate}
            notes={pending.notes}
          />
        )}
        {pending?.status === 'Approved' && (
          <ApprovedAlert approvedDate={pending.approvedDate} />
        )}

        <Collapse in={expanded}>
          <Divider />
          <Box sx={{ p: 2 }}>
            {hasNoAccreditation && (
              <Typography color="text.secondary" variant="body2">
                No ANCC accreditation has been created for this{' '}
                {eventId ? 'event' : 'course'}.
              </Typography>
            )}

            {hasBoth && finalized && pending && (
              <Box>
                <Box className="grid grid-cols-2 gap-4 mb-4">
                  <Typography
                    color="text.secondary"
                    variant="caption"
                    sx={{ fontWeight: 600 }}
                  >
                    Current (Finalized)
                  </Typography>
                  <Typography
                    color="text.secondary"
                    variant="caption"
                    sx={{ fontWeight: 600 }}
                  >
                    Pending Update
                  </Typography>
                </Box>
                <FieldComparison
                  label="Course Code"
                  finalizedValue={finalized.courseCode}
                  pendingValue={pending.courseCode}
                />
                <FieldComparison
                  label="Expiration Date"
                  finalizedValue={finalized.expirationDate}
                  pendingValue={pending.expirationDate}
                  isDate
                />
                <FieldComparison
                  label="Target Audience"
                  finalizedValue={finalized.targetAudience.join(', ')}
                  pendingValue={pending.targetAudience.join(', ')}
                />
                <FieldComparison
                  label="Educational Needs"
                  finalizedValue={finalized.educationalNeeds.join(', ')}
                  pendingValue={pending.educationalNeeds.join(', ')}
                />
                {pending.notes && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paperDark' }}>
                    <Typography
                      color="text.secondary"
                      variant="caption"
                      sx={{ fontWeight: 600 }}
                    >
                      Notes
                    </Typography>
                    <Typography variant="body2">{pending.notes}</Typography>
                  </Box>
                )}
                <StatusActions
                  status={pending.status}
                  submitting={submitting}
                  onEdit={() => setEditFormOpen(true)}
                  onDelete={() => setConfirm('delete-pending-ancc')}
                  onFinalize={handleFinalize}
                />
              </Box>
            )}

            {hasOnlyFinalized && finalized && (
              <Box className="grid gap-3">
                <Box>
                  <Typography color="text.secondary" variant="caption">
                    Course Code
                  </Typography>
                  <Typography variant="body2">
                    {finalized.courseCode}
                  </Typography>
                </Box>
                {finalized.expirationDate && (
                  <Box>
                    <Typography color="text.secondary" variant="caption">
                      Expiration Date
                    </Typography>
                    <Typography variant="body2">
                      {dayjs(finalized.expirationDate).format('MMM D, YYYY')}
                    </Typography>
                  </Box>
                )}
                <Box className="grid grid-cols-2 gap-4">
                  <Box>
                    <Typography color="text.secondary" variant="caption">
                      Target Audience
                    </Typography>
                    <Typography variant="body2">
                      {finalized.targetAudience.join(', ')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="caption">
                      Educational Needs
                    </Typography>
                    <Typography variant="body2">
                      {finalized.educationalNeeds.join(', ')}
                    </Typography>
                  </Box>
                </Box>
                <Box className="flex justify-end mt-3">
                  <Button
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => setConfirm('delete-finalized-ancc')}
                    disabled={submitting}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            )}

            {hasOnlyPending && pending && (
              <Box>
                <Box className="grid gap-3">
                  {pending.courseCode && (
                    <Box>
                      <Typography color="text.secondary" variant="caption">
                        Course Code
                      </Typography>
                      <Typography variant="body2">
                        {pending.courseCode}
                      </Typography>
                    </Box>
                  )}
                  {pending.expirationDate && (
                    <Box>
                      <Typography color="text.secondary" variant="caption">
                        Expiration Date
                      </Typography>
                      <Typography variant="body2">
                        {dayjs(pending.expirationDate).format('MMM D, YYYY')}
                      </Typography>
                    </Box>
                  )}
                  {(pending.targetAudience.length > 0 ||
                    pending.educationalNeeds.length > 0) && (
                    <Box className="grid grid-cols-2 gap-4">
                      <Box>
                        <Typography color="text.secondary" variant="caption">
                          Target Audience
                        </Typography>
                        <Typography variant="body2">
                          {pending.targetAudience.join(', ') || '—'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography color="text.secondary" variant="caption">
                          Educational Needs
                        </Typography>
                        <Typography variant="body2">
                          {pending.educationalNeeds.join(', ') || '—'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  <AutoTrackedDates
                    submittedDate={pending.submittedDate}
                    approvedDate={pending.approvedDate}
                    rejectedDate={pending.rejectedDate}
                  />
                  {pending.notes && (
                    <Box sx={{ p: 2, bgcolor: 'background.paperDark' }}>
                      <Typography
                        color="text.secondary"
                        variant="caption"
                        sx={{ fontWeight: 600 }}
                      >
                        Notes
                      </Typography>
                      <Typography variant="body2">{pending.notes}</Typography>
                    </Box>
                  )}
                </Box>
                <StatusActions
                  status={pending.status}
                  submitting={submitting}
                  onEdit={() => setEditFormOpen(true)}
                  onDelete={() => setConfirm('delete-pending-ancc')}
                  onFinalize={handleFinalize}
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Card>

      <CreateForm
        eventId={eventId}
        courseId={courseId}
        open={formOpen}
        setOpen={setFormOpen}
        onSuccess={() => {}}
      />
      {pending && (
        <EditForm
          eventId={eventId}
          courseId={courseId}
          pending={pending}
          finalized={finalized}
          open={editFormOpen}
          setOpen={setEditFormOpen}
          onSuccess={() => {}}
        />
      )}
      {pending && (
        <ConfirmAction
          open={confirm}
          setOpen={setConfirm}
          openText="delete-pending-ancc"
          title="Delete Pending ANCC?"
          text="Are you sure you want to delete this pending ANCC accreditation? This action cannot be undone."
          confirmText="Delete"
          executeAction={async () => {
            if (eventId) {
              dispatch(deletePendingANCCEvent({ id: pending.id, eventId }))
            } else if (courseId) {
              dispatch(deletePendingANCCCourse({ id: pending.id, courseId }))
            }
          }}
        />
      )}
      {finalized && (
        <ConfirmAction
          open={confirm}
          setOpen={setConfirm}
          openText="delete-finalized-ancc"
          title="Delete ANCC Accreditation?"
          text="Are you sure you want to delete this ANCC accreditation? This action cannot be undone."
          confirmText="Delete"
          executeAction={async () => {
            if (eventId) {
              dispatch(deleteANCCEvent({ id: finalized.id, eventId }))
            } else if (courseId) {
              dispatch(deleteANCCCourse({ id: finalized.id, courseId }))
            }
          }}
        />
      )}
      <OperationSnackbars
        successMessage={operationSuccess}
        errorMessage={operationError}
        onClear={() => dispatch(clearAnccOperationStatus())}
      />
    </Box>
  )
}
